import { useEffect } from 'react'
import { useMotion } from '@/components/motion/motion'
import { pageEvent, type PageEventDetail } from '@/components/motion/pageEvents'
import { pagedViewportQuery } from '@/components/motion/viewport'
import { createWheelPaging, wheelPixels } from '@/components/motion/wheelIntent'

/* Magnetic scroll (Home, How it works), on screens that hold one section per screen: each wheel or
   trackpad gesture, and each paging key, moves one screen, animated here. There is deliberately no
   CSS scroll snapping: Safari's snapping pulls the page back against scripted scrolling, and on
   phones it fought touch scrolling. Touch and the scrollbar scroll freely everywhere. */

/** Where paging stops: each section's top, the end of any section taller than the screen, the
    invitation centred, and the very top and bottom of the page. */
const pageStops = (sectionSelector: string) => {
  const viewportHeight = window.innerHeight || 800
  const max = document.documentElement.scrollHeight - viewportHeight
  const stops = [0, max]
  document.querySelectorAll<HTMLElement>(sectionSelector).forEach((section) => {
    if (section.offsetParent === null) return
    const rect = section.getBoundingClientRect()
    const top = rect.top + window.scrollY
    if (section.classList.contains('x-invite')) {
      stops.push(top - (viewportHeight - rect.height) / 2)
      return
    }
    stops.push(top)
    if (rect.height > viewportHeight + 40) stops.push(top + rect.height - viewportHeight)
  })
  return [...new Set(stops.map((y) => Math.round(Math.min(max, Math.max(0, y)))))].sort((a, b) => a - b)
}

const easeInOut = (progress: number) => (progress < 0.5 ? 4 * progress ** 3 : 1 - (-2 * progress + 2) ** 3 / 2)

/** `sectionSelector`: the sections that each take a screen. The invitation (.x-invite) is centred. */
export const useSectionPaging = (sectionSelector: string) => {
  const { reduceMotion } = useMotion()

  useEffect(() => {
    const query = window.matchMedia?.(pagedViewportQuery)
    const root = document.documentElement
    const wheelPaging = createWheelPaging()

    let frame = 0
    let inFlight = false
    let destination = 0
    // Moves end on the clock, not on their last frame, which a busy or hidden page can delay.
    let moveEnd = 0

    const active = () => query?.matches ?? false
    const moving = () => performance.now() < moveEnd

    const stop = () => {
      cancelAnimationFrame(frame)
      inFlight = false
      root.style.scrollBehavior = ''
    }

    // Frame by frame, so the move looks the same in every browser and it is known when it ends.
    const scrollToStop = (target: number) => {
      stop()
      // The site scrolls smoothly for anchor links, which would turn each frame into an animation.
      root.style.scrollBehavior = 'auto'
      const start = window.scrollY
      const distance = target - start
      destination = target
      if (reduceMotion || !distance) {
        window.scrollTo(0, target)
        root.style.scrollBehavior = ''
        return
      }
      const duration = Math.min(900, 560 + Math.abs(distance) * 0.12)
      const startTime = performance.now()
      let expected = start
      moveEnd = startTime + duration
      inFlight = true
      const step = (now: number) => {
        // The visitor took over (the scrollbar, a touch screen): let them.
        if (Math.abs(window.scrollY - expected) > 3) {
          stop()
          return
        }
        const progress = Math.min(1, Math.max(0, (now - startTime) / duration))
        window.scrollTo(0, start + distance * easeInOut(progress))
        expected = window.scrollY
        if (progress < 1) frame = requestAnimationFrame(step)
        else stop()
      }
      frame = requestAnimationFrame(step)
    }

    const goPage = (direction: number) => {
      // A move whose last frames are running a little late still counts as arrived.
      const y = inFlight && performance.now() - moveEnd < 300 ? destination : window.scrollY
      const stops = pageStops(sectionSelector)
      const target = direction > 0 ? stops.find((t) => t > y + 4) : [...stops].reverse().find((t) => t < y - 4)
      if (target === undefined) return false
      scrollToStop(target)
      window.dispatchEvent(new CustomEvent<PageEventDetail>(pageEvent, { detail: { target } }))
      return true
    }

    const onWheel = (event: WheelEvent) => {
      if (!active() || event.ctrlKey || Math.abs(event.deltaX) > Math.abs(event.deltaY)) return
      event.preventDefault()
      const direction = wheelPaging(event.timeStamp || performance.now(), wheelPixels(event), moving())
      if (direction) goPage(direction)
    }

    const onKeyDown = (event: KeyboardEvent) => {
      if (!active() || event.altKey || event.metaKey || event.ctrlKey) return
      const target = event.target as HTMLElement | null
      if (target && (target.isContentEditable || /^(INPUT|TEXTAREA|SELECT|BUTTON)$/.test(target.tagName))) return
      let direction = 0
      if (['ArrowDown', 'PageDown'].includes(event.key) || (event.key === ' ' && !event.shiftKey)) direction = 1
      if (['ArrowUp', 'PageUp'].includes(event.key) || (event.key === ' ' && event.shiftKey)) direction = -1
      if (!direction) return
      if (moving()) {
        event.preventDefault()
        return
      }
      if (goPage(direction)) event.preventDefault()
    }

    window.addEventListener('wheel', onWheel, { passive: false })
    window.addEventListener('keydown', onKeyDown)
    return () => {
      stop()
      window.removeEventListener('wheel', onWheel)
      window.removeEventListener('keydown', onKeyDown)
    }
  }, [reduceMotion, sectionSelector])
}
